import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import "firebase/compat/storage";
import "firebase/compat/functions";
import { getApp } from "firebase/app";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Consolidate config into a single object for clarity.
// measurementId is omitted so the SDK uses the value from the server; including a local value
// (e.g. REACT_APP_FIREBASE_MEASUREMENT_ID) when the server returns a different ID or undefined
// triggers: "The measurement ID in the local Firebase config does not match the measurement ID
// fetched from the server." Configure Analytics / Measurement ID in Firebase Console if needed.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/** Match deployed Gen1 region (lotsogService and firebase deploy). */
const FUNCTIONS_REGION = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "us-central1";
const FUNCTIONS_EMULATOR_HOST = "127.0.0.1";
const FUNCTIONS_EMULATOR_PORT = 5001;
const FUNCTIONS_BACKEND_MODE_ENV = String(process.env.REACT_APP_FUNCTIONS_BACKEND_MODE || "")
  .trim()
  .toLowerCase();
const FUNCTIONS_BACKEND_MODE = ["auto", "hosted", "emulator"].includes(FUNCTIONS_BACKEND_MODE_ENV)
  ? FUNCTIONS_BACKEND_MODE_ENV
  : "auto";

function isLocalWebHostname(hostname) {
  if (!hostname) return false;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1"
  );
}

function shouldUseFunctionsEmulator() {
  if (FUNCTIONS_BACKEND_MODE === "hosted") return false;
  return (
    process.env.NODE_ENV !== "test" &&
    typeof window !== "undefined" &&
    (FUNCTIONS_BACKEND_MODE === "emulator" || process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "true") &&
    isLocalWebHostname(window.location?.hostname)
  );
}

// Use initializeTestApp for test environment, regular initializeApp for production.
// In dev (e.g. HMR) the module can re-run; avoid duplicate-app by only initializing once.
const firebaseApp =
  process.env.NODE_ENV === "test"
    ? firebase.initializeTestApp({
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        auth: { uid: "test-user", email: "test@example.com" },
      })
    : (firebase.apps?.length ? firebase.app() : firebase.initializeApp(firebaseConfig));

// Only initialize analytics in production
if (process.env.NODE_ENV === "production") {
  firebase.analytics();
}

// Wire Functions emulator for both modular getFunctions() and compat firebase.functions()
// (httpsCallable in lotsogService and elsewhere uses compat).
if (shouldUseFunctionsEmulator()) {
  try {
    connectFunctionsEmulator(
      getFunctions(getApp(), FUNCTIONS_REGION),
      FUNCTIONS_EMULATOR_HOST,
      FUNCTIONS_EMULATOR_PORT
    );
  } catch (e) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[Firebase] connectFunctionsEmulator (app firebase.js):", e?.message || e);
    }
  }
  try {
    firebaseApp.functions(FUNCTIONS_REGION).useEmulator(FUNCTIONS_EMULATOR_HOST, FUNCTIONS_EMULATOR_PORT);
  } catch (e) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[Firebase] compat functions.useEmulator:", e?.message || e);
    }
  }
  if (typeof console !== "undefined" && console.info) {
    console.info(
      `[Firebase] Functions emulator (${FUNCTIONS_EMULATOR_HOST}:${FUNCTIONS_EMULATOR_PORT}). Run: firebase emulators:start --only functions`
    );
  }
}
if (typeof window !== "undefined" && process.env.NODE_ENV !== "test" && typeof console !== "undefined" && console.info) {
  const effectiveMode = shouldUseFunctionsEmulator() ? "emulator" : "hosted";
  console.info(
    `[Firebase] Functions backend: ${effectiveMode} (project=${firebaseConfig.projectId || "unknown"}, region=${FUNCTIONS_REGION}, mode=${FUNCTIONS_BACKEND_MODE})`
  );
}

const db = firebaseApp.firestore();
// Dev / restrictive networks: long-polling can avoid some WebChannel issues (and some ad blockers
// mis-classify WebChannel traffic). Safe no-op if settings were already applied (e.g. HMR).
if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  isLocalWebHostname(window.location?.hostname)
) {
  try {
    db.settings({ experimentalAutoDetectLongPolling: true });
  } catch (_) {
    /* Firestore may already be in use */
  }
}
const storage = firebaseApp.storage();

export default firebaseApp;
export { db, storage };
